import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add, Delete, Person } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { orderAPI, lookupAPI } from '../services/api';
import { Store, Staff, Customer, OrderItem, OrderRequest, NewCustomer, Manufacturer } from '../types';

interface OrderFormProps {
  selectedStore?: Store | null;
}

const OrderForm: React.FC<OrderFormProps> = ({ selectedStore }) => {
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [storeId, setStoreId] = useState<number | ''>('');
  const [staffId, setStaffId] = useState<number | ''>('');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [status, setStatus] = useState<'pending' | 'ordered' | 'delivery-pending' | 'delivered' | 'cancelled' | 'not-available' | 'partially-available'>('pending');
  const [items, setItems] = useState<OrderItem[]>([{ title: '', manufacturer_id: 0 }]);
  const [itemInventory, setItemInventory] = useState<{[index: number]: { quantity: number; message: string } | null}>({});

  const [stores, setStores] = useState<Store[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [medicines, setMedicines] = useState<Manufacturer[]>([]);
  const [medicineSearchTerms, setMedicineSearchTerms] = useState<{[index: number]: string}>({});
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // New customer dialog
  const [newCustomerDialog, setNewCustomerDialog] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerNumber, setNewCustomerNumber] = useState('');

  // New medicine dialog
  const [newMedicineDialog, setNewMedicineDialog] = useState(false);
  const [newMedicineName, setNewMedicineName] = useState('');
  const [newManufacturerName, setNewManufacturerName] = useState('');
  const [newMedicineCombination, setNewMedicineCombination] = useState('');
  const [newMedicinePackSize, setNewMedicinePackSize] = useState('');
  const [currentMedicineIndex, setCurrentMedicineIndex] = useState<number | null>(null);
  const [manufacturerNames, setManufacturerNames] = useState<string[]>([]);
  const [filteredManufacturerNames, setFilteredManufacturerNames] = useState<string[]>([]);

  // Order confirmation dialog
  const [orderConfirmDialog, setOrderConfirmDialog] = useState(false);

  useEffect(() => {
    loadLookupData();
    // Network connectivity test
    fetch(`${process.env.REACT_APP_API_URL}/health`)
      .then(response => {
        console.log('Direct health check response:', response.status);
        return response.json();
      })
      .then(data => {
        console.log('Direct health check data:', data);
      })
      .catch(error => {
        console.error('Direct health check failed:', error);
      });
  }, []);

  // Set store ID when selectedStore is available
  useEffect(() => {
    if (selectedStore) {
      setStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  const loadLookupData = async () => {
    try {
      console.log('API Base URL:', process.env.REACT_APP_API_URL || 'http://localhost:5000/api');
      const [storesData, staffData, customersData, manufacturerNamesData] = await Promise.all([
        lookupAPI.getStores(),
        lookupAPI.getStaff(),
        lookupAPI.getCustomers(),
        lookupAPI.getManufacturerNames(),
      ]);
      setStores(storesData);
      setStaff(staffData);
      setCustomers(customersData);
      setFilteredCustomers(customersData);
      setManufacturerNames(manufacturerNamesData);
      setFilteredManufacturerNames(manufacturerNamesData);
    } catch (error: any) {
      console.error('Error loading lookup data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorDetails = error?.response?.data || error?.response || error;
      console.error('Detailed error:', errorDetails);
      setAlert({ 
        type: 'error', 
        message: `Error loading form data: ${errorMessage}. Check console for details.` 
      });
    }
  };

  const handleCustomerSearch = (searchValue: string) => {
    setCustomerName(searchValue);
    
    // Warn if user types only numbers in customer name field
    if (searchValue.trim() && /^[0-9]+$/.test(searchValue.trim())) {
      setAlert({ 
        type: 'error', 
        message: 'Please enter customer name, not phone number. Use the Mobile Number field for phone numbers.' 
      });
    }
    
    if (searchValue.trim()) {
      const filtered = customers.filter(
        customer =>
          (customer.customer_name && customer.customer_name.toLowerCase().includes(searchValue.toLowerCase())) ||
          (customer.customer_number && customer.customer_number.toString().includes(searchValue))
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
      setCustomerId('');
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setCustomerId(customer.id);
    setCustomerName(customer.customer_name || '');
    setMobileNumber(customer.customer_number ? customer.customer_number.toString() : '');
  };

  const handleAddNewCustomer = async () => {
    if (!newCustomerName.trim() || !newCustomerNumber.trim()) {
      setAlert({ type: 'error', message: 'Both customer name and mobile number are required' });
      return;
    }

    // Validate that name is not just a phone number
    if (/^[0-9]+$/.test(newCustomerName.trim())) {
      setAlert({ type: 'error', message: 'Customer name cannot be just a phone number. Please enter a proper name.' });
      return;
    }

    // Validate mobile number is numeric
    if (!/^[0-9]+$/.test(newCustomerNumber.trim())) {
      setAlert({ type: 'error', message: 'Mobile number must contain only digits' });
      return;
    }

    try {
      const newCustomer: NewCustomer = {
        customer_name: newCustomerName.trim(),
        customer_number: parseInt(newCustomerNumber),
        created_by: 'system',
      };

      const createdCustomer = await lookupAPI.createCustomer(newCustomer);
      setCustomers(prev => [...prev, createdCustomer]);
      setFilteredCustomers(prev => [...prev, createdCustomer]);
      
      // Select the new customer
      handleCustomerSelect(createdCustomer);
      
      setNewCustomerDialog(false);
      setNewCustomerName('');
      setNewCustomerNumber('');
      setAlert({ type: 'success', message: 'Customer created successfully!' });
    } catch (error) {
      console.error('Error creating customer:', error);
      setAlert({ type: 'error', message: 'Error creating customer' });
    }
  };

  const handleManufacturerSearch = (searchValue: string) => {
    setNewManufacturerName(searchValue);
    if (searchValue.trim()) {
      const filtered = manufacturerNames.filter(name =>
        name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredManufacturerNames(filtered);
    } else {
      setFilteredManufacturerNames(manufacturerNames);
    }
  };

  const handleOpenMedicineDialog = (index: number, medicineName: string) => {
    setCurrentMedicineIndex(index);
    setNewMedicineName(medicineName);
    setNewManufacturerName('');
    setNewMedicineCombination('');
    setNewMedicinePackSize('');
    setFilteredManufacturerNames(manufacturerNames);
    setNewMedicineDialog(true);
  };

  const handleAddNewMedicine = async () => {
    if (!newMedicineName.trim()) {
      return;
    }

    try {
      const newMedicine = {
        medicine_name: newMedicineName.trim(),
        manufacturer_name: newManufacturerName.trim() || 'N/A',
        combination: newMedicineCombination.trim() || undefined,
        pack_size: newMedicinePackSize.trim() || undefined,
        created_by: 'system',
      };

      const createdMedicine = await lookupAPI.createMedicine(newMedicine);
      
      // Update the current medicine item
      if (currentMedicineIndex !== null) {
        const updatedItems = [...items];
        updatedItems[currentMedicineIndex] = {
          ...updatedItems[currentMedicineIndex],
          title: createdMedicine.medicine_name,
          manufacturer_id: createdMedicine.id,
          manufacturer_name: createdMedicine.manufacturer_name,
          medicine_name: createdMedicine.medicine_name,
          combination: createdMedicine.combination,
          pack_size: createdMedicine.pack_size
        };
        setItems(updatedItems);
        setMedicineSearchTerms(prev => ({ 
          ...prev, 
          [currentMedicineIndex]: createdMedicine.medicine_name 
        }));
      }
      
      // Refresh medicines list and manufacturer names
      loadLookupData();
      
      setNewMedicineDialog(false);
      setNewMedicineName('');
      setNewManufacturerName('');
      setNewMedicineCombination('');
      setNewMedicinePackSize('');
      setCurrentMedicineIndex(null);
      setAlert({ type: 'success', message: 'Medicine created successfully!' });
    } catch (error) {
      console.error('Error creating medicine:', error);
      setAlert({ type: 'error', message: 'Error creating medicine' });
    }
  };

  const handleMedicineSearch = async (searchValue: string, index: number) => {
    setMedicineSearchTerms(prev => ({ ...prev, [index]: searchValue }));
    setActiveSearchIndex(index);
    
    if (searchValue.trim().length >= 2) {
      // Check if store is selected before searching
      if (!storeId) {
        setAlert({ type: 'error', message: 'Please select a store before searching for medicines' });
        setMedicines([]);
        return;
      }
      
      try {
        const searchResults = await lookupAPI.searchMedicines(searchValue, storeId as number);
        setMedicines(searchResults);
      } catch (error) {
        console.error('Error searching medicines:', error);
        setMedicines([]);
        setAlert({ type: 'error', message: 'Error searching medicines. Please try again.' });
      }
    } else {
      setMedicines([]);
    }
  };

  const handleMedicineSelect = async (medicine: Manufacturer, index: number) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      title: medicine.medicine_name,
      manufacturer_id: medicine.id,
      manufacturer_name: medicine.manufacturer_name,
      medicine_name: medicine.medicine_name,
      combination: medicine.combination,
      pack_size: medicine.pack_size
    };
    setItems(updatedItems);
    setMedicineSearchTerms(prev => ({ ...prev, [index]: medicine.medicine_name }));
    setMedicines([]);
    setActiveSearchIndex(null);

    // Fetch inventory data for the selected medicine
    if (storeId && medicine.id) {
      try {
        const inventoryData = await lookupAPI.getMedicineInventory(medicine.id, storeId);
        if (inventoryData && inventoryData.quantity > 0) {
          // Store inventory data for this item
          setItemInventory(prev => ({
            ...prev,
            [index]: {
              quantity: inventoryData.quantity,
              message: `This item has ${inventoryData.quantity} quantity in last inventory snapshot. If item has low or no inventory, please update System inventory.`
            }
          }));
        }
      } catch (error) {
        console.error('Error fetching inventory:', error);
        setItemInventory(prev => ({
          ...prev,
          [index]: {
            quantity: 0,
            message: 'Unable to fetch inventory data. Please check System inventory.'
          }
        }));
      }
    }
  };

  const handleMedicineBlur = (index: number) => {
    // Use setTimeout to allow click events on dropdown items to fire first
    setTimeout(() => {
      if (activeSearchIndex === index) {
        setMedicines([]);
        setActiveSearchIndex(null);
      }
    }, 200);
  };

  const handleAddItem = () => {
    setItems([...items, { title: '', manufacturer_id: 0 }]);
    // Clear any existing inventory data for the new item
    setItemInventory(prev => ({ ...prev, [items.length]: null }));
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
      // Clear active search if removing the active search item
      if (activeSearchIndex === index) {
        setActiveSearchIndex(null);
        setMedicines([]);
      }
      // Update medicineSearchTerms to shift indices after removal
      const newSearchTerms: {[index: number]: string} = {};
      Object.keys(medicineSearchTerms).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newSearchTerms[keyIndex] = medicineSearchTerms[keyIndex];
        } else if (keyIndex > index) {
          newSearchTerms[keyIndex - 1] = medicineSearchTerms[keyIndex];
        }
      });
      setMedicineSearchTerms(newSearchTerms);
      // Update activeSearchIndex if it's after the removed item
      if (activeSearchIndex !== null && activeSearchIndex > index) {
        setActiveSearchIndex(activeSearchIndex - 1);
      }
      // Update itemInventory to shift indices after removal
      const newItemInventory: {[index: number]: { quantity: number; message: string } | null} = {};
      Object.keys(itemInventory).forEach(key => {
        const keyIndex = parseInt(key);
        if (keyIndex < index) {
          newItemInventory[keyIndex] = itemInventory[keyIndex];
        } else if (keyIndex > index) {
          newItemInventory[keyIndex - 1] = itemInventory[keyIndex];
        }
      });
      setItemInventory(newItemInventory);
    }
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setItems(updatedItems);
  };

  const handleSubmitClick = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!storeId || !staffId || !date) {
      setAlert({ type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    // Validate customer data before submission
    if (!customerId && customerName.trim() && mobileNumber.trim()) {
      // If creating a new customer, validate the name is not just a phone number
      if (/^[0-9]+$/.test(customerName.trim())) {
        setAlert({ 
          type: 'error', 
          message: 'Customer name cannot be just a phone number. Please enter a proper name or use the "Add Customer" button.' 
        });
        return;
      }
      
      // Validate mobile number is numeric
      if (!/^[0-9]+$/.test(mobileNumber.trim())) {
        setAlert({ 
          type: 'error', 
          message: 'Mobile number must contain only digits' 
        });
        return;
      }
    }

    // Validate medicine items
    const validItems = items.filter(item => item.title.trim());
    const itemsWithoutQuantity = validItems.filter(item => !item.quantity || item.quantity <= 0);
    
    if (validItems.length === 0) {
      setAlert({ type: 'error', message: 'Please add at least one medicine item' });
      return;
    }
    
    if (itemsWithoutQuantity.length > 0) {
      setAlert({ type: 'error', message: 'Please enter quantity for all medicine items' });
      return;
    }

    // Show confirmation dialog
    setOrderConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setOrderConfirmDialog(false);
    setLoading(true);
    
    try {
      const orderData: any = {
        date: date.format('YYYY-MM-DD'),
        staff_id: staffId as number,
        store_id: storeId as number,
        customer_id: customerId || undefined,
        status,
        items: items.filter(item => item.title.trim()),
        created_by: 'system',
        title: `Order - ${date.format('YYYY-MM-DD')}`,
      };

      // Add customer info for auto-creation if no existing customer is selected
      if (!customerId && customerName.trim() && mobileNumber.trim()) {
        orderData.customer_name = customerName.trim();
        orderData.customer_number = mobileNumber.trim();
      }

      await orderAPI.create(orderData);
      
      // Refresh customer list if new customer was created
      if (!customerId && customerName.trim() && mobileNumber.trim()) {
        loadLookupData();
      }
      
      // Reset form
      setDate(dayjs());
      // Only reset store if no selectedStore is provided
      if (!selectedStore) {
        setStoreId('');
      }
      setStaffId('');
      setCustomerId('');
      setMobileNumber('');
      setCustomerName('');
      setStatus('pending');
      setItems([{ title: '', manufacturer_id: 0 }]);
      setMedicineSearchTerms({});
      setMedicines([]);
      setActiveSearchIndex(null);
      setItemInventory({});
      
      setAlert({ type: 'success', message: 'Order created successfully!' });
    } catch (error) {
      console.error('Error creating order:', error);
      setAlert({ type: 'error', message: 'Error creating order' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" gutterBottom color="primary">
          Create New Order
        </Typography>

        {alert && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert(null)}
            sx={{ mb: 2 }}
          >
            {alert.message}
          </Alert>
        )}

        <form onSubmit={handleSubmitClick}>
          <Grid container spacing={3}>
            {/* Date */}
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Date *"
                value={date}
                onChange={(newValue) => setDate(newValue || dayjs())}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            {/* Store */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Store</InputLabel>
                <Select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value as number)}
                  label="Store"
                  disabled={!!selectedStore}
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.store_name}
                    </MenuItem>
                  ))}
                </Select>
                {selectedStore && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                    Pre-selected from store selection. Use "Change Store" in header to modify.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Staff */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Staff</InputLabel>
                <Select
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value as number)}
                  label="Staff"
                >
                  {staff.map((staffMember) => (
                    <MenuItem key={staffMember.id} value={staffMember.id}>
                      {staffMember.staff_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Customer Name */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Customer Name"
                value={customerName}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                placeholder="Search existing customer or enter new name"
                helperText="Type to search existing customers"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setNewCustomerDialog(true)}
                      size="small"
                      color="primary"
                    >
                      <Person />
                    </IconButton>
                  ),
                }}
              />
              {customerName && filteredCustomers.length > 0 && !customerId && (
                <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                  {filteredCustomers.slice(0, 5).map((customer) => (
                    <Chip
                      key={customer.id}
                      label={`${customer.customer_name} - ${customer.customer_number}`}
                      onClick={() => handleCustomerSelect(customer)}
                      sx={{ m: 0.5 }}
                      variant="outlined"
                      clickable
                    />
                  ))}
                </Box>
              )}
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                type="tel"
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="ordered">Ordered</MenuItem>
                  <MenuItem value="delivery-pending">Delivery Pending</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="not-available">Not Available</MenuItem>
                  <MenuItem value="partially-available">Partially Available</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Medicine Items */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Medicine Items
              </Typography>
              {items.map((item, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={12} md={5}>
                      <Box position="relative">
                        <TextField
                          fullWidth
                          label="Medicine Name *"
                          value={medicineSearchTerms[index] || item.title || ''}
                          onChange={(e) => handleMedicineSearch(e.target.value, index)}
                          onBlur={() => handleMedicineBlur(index)}
                          placeholder="Search medicine by name..."
                          required
                          helperText={item.manufacturer_name ? `Manufacturer: ${item.manufacturer_name}` : 'Type at least 2 characters to search'}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={() => handleOpenMedicineDialog(index, medicineSearchTerms[index] || '')}
                                size="small"
                                color="secondary"
                                title="Create New Medicine"
                              >
                                <Add />
                              </IconButton>
                            ),
                          }}
                        />
                        {medicines.length > 0 && activeSearchIndex === index && medicineSearchTerms[index] && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              bgcolor: 'background.paper',
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              maxHeight: 200,
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: 2
                            }}
                          >
                            {medicines.slice(0, 10).map((medicine) => (
                              <Box
                                key={medicine.id}
                                onClick={() => handleMedicineSelect(medicine, index)}
                                sx={{
                                  p: 1.5,
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  {medicine.medicine_name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {medicine.manufacturer_name} 
                                  {medicine.combination && ` | ${medicine.combination}`}
                                  {medicine.pack_size && ` | ${medicine.pack_size}`}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Pack Size"
                        value={item.pack_size || ''}
                        onChange={(e) => handleItemChange(index, 'pack_size', e.target.value)}
                        disabled={!!item.manufacturer_id}
                        helperText={item.manufacturer_id ? 'Auto-filled from selection' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      {/* Inventory Message */}
                      {itemInventory[index] && (
                        <Box sx={{ mb: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '1rem',
                              fontWeight: 500,
                              backgroundColor: '#FF9800',
                              color: '#000000',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              border: '1px solid #E65100'
                            }}
                          >
                            Warning: {itemInventory[index]!.message}
                          </Typography>
                        </Box>
                      )}
                      <TextField
                        fullWidth
                        label="Quantity *"
                        type="number"
                        value={item.quantity || ''}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                        error={!!(item.title && (!item.quantity || item.quantity <= 0))}
                        helperText={item.title && (!item.quantity || item.quantity <= 0) ? 'Quantity is required' : ''}
                        required
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={1}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row', md: 'column' }, gap: 1, justifyContent: { xs: 'center', sm: 'center', md: 'flex-start' } }}>
                        <IconButton
                          onClick={handleAddItem}
                          color="primary"
                          size="small"
                          sx={{ 
                            bgcolor: 'primary.main', 
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' }
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleRemoveItem(index)}
                          color="error"
                          disabled={items.length === 1}
                          size="small"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                <Button
                  startIcon={<Add />}
                  onClick={handleAddItem}
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ 
                    minWidth: { xs: '100%', sm: 180 },
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  Add Medicine
                </Button>
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  Add all medicines before submitting the order
                </Typography>
              </Box>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 'auto' },
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? 'Creating...' : 'Create Order'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* New Customer Dialog */}
        <Dialog open={newCustomerDialog} onClose={() => setNewCustomerDialog(false)}>
          <DialogTitle>Add New Customer</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Customer Name"
              fullWidth
              variant="outlined"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Mobile Number"
              fullWidth
              variant="outlined"
              type="tel"
              value={newCustomerNumber}
              onChange={(e) => setNewCustomerNumber(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewCustomerDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNewCustomer} variant="contained">
              Add Customer
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Medicine Dialog */}
        <Dialog open={newMedicineDialog} onClose={() => setNewMedicineDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add New Medicine</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  autoFocus
                  label="Medicine Name *"
                  fullWidth
                  variant="outlined"
                  value={newMedicineName}
                  onChange={(e) => setNewMedicineName(e.target.value)}
                  helperText="Enter the name of the medicine"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box position="relative">
                  <TextField
                    label="Manufacturer Name"
                    fullWidth
                    variant="outlined"
                    value={newManufacturerName}
                    onChange={(e) => handleManufacturerSearch(e.target.value)}
                    placeholder="Type to search existing manufacturers"
                    helperText="Leave empty to set as 'N/A'"
                  />
                  {newManufacturerName && filteredManufacturerNames.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        bgcolor: 'background.paper',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        maxHeight: 150,
                        overflowY: 'auto',
                        zIndex: 1300,
                        boxShadow: 2
                      }}
                    >
                      {filteredManufacturerNames.slice(0, 8).map((name, idx) => (
                        <Box
                          key={idx}
                          onClick={() => {
                            setNewManufacturerName(name);
                            setFilteredManufacturerNames([]);
                          }}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            borderBottom: idx < filteredManufacturerNames.length - 1 ? '1px solid #f0f0f0' : 'none',
                            '&:hover': { bgcolor: '#f5f5f5' }
                          }}
                        >
                          <Typography variant="body2">{name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Combination"
                  fullWidth
                  variant="outlined"
                  value={newMedicineCombination}
                  onChange={(e) => setNewMedicineCombination(e.target.value)}
                  placeholder="e.g., Paracetamol + Caffeine"
                  helperText="Optional: Active ingredients combination"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Pack Size"
                  fullWidth
                  variant="outlined"
                  value={newMedicinePackSize}
                  onChange={(e) => setNewMedicinePackSize(e.target.value)}
                  placeholder="e.g., 10 tablets, 100ml"
                  helperText="Optional: Package size information"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewMedicineDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddNewMedicine} 
              variant="contained"
              disabled={!newMedicineName.trim()}
            >
              Add Medicine
            </Button>
          </DialogActions>
        </Dialog>

        {/* Order Confirmation Dialog */}
        <Dialog open={orderConfirmDialog} onClose={() => setOrderConfirmDialog(false)}>
          <DialogTitle>Confirm Order Submission</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Please review your order details before submitting:
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Date:</strong> {date.format('DD/MM/YYYY')}</Typography>
              <Typography variant="body2"><strong>Store:</strong> {stores.find(s => s.id === storeId)?.store_name || 'Not selected'}</Typography>
              <Typography variant="body2"><strong>Staff:</strong> {staff.find(s => s.id === staffId)?.staff_name || 'Not selected'}</Typography>
              <Typography variant="body2"><strong>Customer:</strong> {customerName || 'Not specified'}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {status}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><strong>Medicines ({items.filter(item => item.title.trim()).length}):</strong></Typography>
              {items.filter(item => item.title.trim()).map((item, index) => (
                <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                  • {item.title} {item.quantity ? `(Qty: ${item.quantity})` : <span style={{ color: 'red' }}>(Qty: Not specified)</span>}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              ⚠️ Have you added all medicines to this order?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOrderConfirmDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmSubmit} variant="contained" color="primary">
              Confirm & Submit Order
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default OrderForm;
